import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import CommonFields from "../common/CommonField";
import {
  Grid,
  Column,
  Toggle,
  FormGroup,
  Stack,
  Tile,
  ToastNotification,
} from "@carbon/react";

const Amenities = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 4,
  lastSavedStep = 0,
  isEditMode = false,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onBack,
  CommonButtons,
  onNavigateNext,
  contactData,
  request_type = "form_attributes",
}) => {
  console.log('🔍 Amenities (Step 4) received:', {
    formMode,
    currentStep,
    lastSavedStep,
    isEditMode,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
    fullData: data, 
    amenities: data?.amenities, 
    attributes: data?.attributes 
  });

  const isEditForm = useMemo(() => {
    return formMode === "edit" || lastSavedStep >= currentStep;
  }, [formMode, lastSavedStep, currentStep]);

  const FIELD_MAP = useMemo(() => ({
    waterSupply: "water_supply",
    electricityConnection: "electricity_connection",
    sewageConnection: "sewage_connection",
    directions: "directions",
    widthOfFacingRoad: "width_facing_road",
    gatedSecurity: "gated_security",
  }), []);

  const [form, setForm] = useState({
    waterSupply: "",
    electricityConnection: "",
    sewageConnection: "",
    widthOfFacingRoad: "",
    gatedSecurity: false,
    directions: "",
  });

  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  // No validation rules - all fields are optional
  const validationRules = useMemo(() => ({}), []);

  const handleBlur = useCallback((fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
  }, []);

  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      return attributeValues
        .filter((a) => a.attrName === apiKey)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
        }));
    },
    [attributeValues, FIELD_MAP]
  );

  useEffect(() => {
    const amenitiesData = data?.amenities || data?.attributes;
    if (!amenitiesData) {
      console.log('💧 No amenities data found to load');
      return;
    }

    const incoming = {};

    Object.entries(FIELD_MAP).forEach(([field, apiKey]) => {
      const raw = amenitiesData[apiKey];
      console.log(`💧 Loading field ${field} (${apiKey}):`, raw);
      
      if (raw == null || raw === "") return;

      if (field === "similarPropertiesAvailable" || field === "gatedSecurity") {
        incoming[field] = 
          raw === 1 || 
          raw === true || 
          String(raw).toLowerCase() === "yes" ||
          String(raw).toLowerCase() === "true";
      } else {
        incoming[field] = raw;
      }
    });

    console.log('💧 Amenities hydrating form with data:', {
      source: data?.amenities ? 'amenities' : 'attributes',
      rawData: amenitiesData,
      mappedData: incoming
    });
    
    setForm((prev) => ({ ...prev, ...incoming }));
  }, [data, FIELD_MAP]);

  const getFieldError = useCallback((field) => {
    const rule = validationRules[field];
    if (!rule) return null;

    const value = form[field];
    if (rule.required) {
      if (value === "" || value === null || value === undefined) {
        return rule.message;
      }
    }
    if (rule.custom) {
      return rule.custom(value);
    }

    return null;
  }, [form, validationRules]);

  const hasErrors = useCallback(() =>
    Object.keys(validationRules).some((field) => !!getFieldError(field)),
    [validationRules, getFieldError]
  );

  const { renderDropdown, renderNumberField } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  const handleSave = useCallback(async (navigateNext = false) => {
    console.log('💾 Amenities handleSave:', { navigateNext, isEditForm, currentStep });
    
    setTouched(
      Object.keys(form).reduce((acc, f) => ({ ...acc, [f]: true }), {})
    );

    if (hasErrors()) {
      setError("Please fix the errors before saving");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      let uuid = propertyUuid;

      if (!uuid && createProperty) {
        console.log('🆕 Creating property from Step 4...');
        uuid = await createProperty();
        onSaveSuccess?.(uuid, form, currentStep);
      }

      const payload = {};

      for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
        const v = form[field];
        if (v == null || v === "") continue;

        if (field === "similarPropertiesAvailable" || field === "gatedSecurity") {
          payload[apiKey] = v ? "Yes" : "No";
        } else {
          payload[apiKey] = v;
        }
      }

      console.log('💾 Saving amenities:', payload);
      await saveSectionAttributes(uuid, payload, form, "amenities");

      setSuccess("Amenities saved successfully!");
      onSaveSuccess?.(uuid, form, currentStep);

      if (navigateNext) {
        console.log('➡️ Navigating to next step after save');
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(err.message || "Failed to save amenities");
    } finally {
      setSaving(false);
    }
  }, [
    form,
    hasErrors,
    isEditForm,
    currentStep,
    propertyUuid,
    createProperty,
    onSaveSuccess,
    FIELD_MAP,
    saveSectionAttributes,
    onNavigateNext
  ]);

  const handleSaveOnly = useCallback(() => handleSave(false), [handleSave]);
  const handleSaveAndNext = useCallback(() => handleSave(true), [handleSave]);

  return (
    <div style={{ backgroundColor: "#fff"}}>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          timeout={4000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <Grid>
        <Column lg={16}>
          <Tile style={{ backgroundColor:"white" }}>
            <div style={{ textAlign: "center", marginBottom: "1rem" }}>
              <p
                style={{
                  color: "#0f62fe",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  maxWidth: "700px",
                  margin: "0 auto",
                  lineHeight: "1.5",
                }}
              >
                Provide additional details about your property to get maximum visibility.
              </p>
            </div>
            
            <Stack gap={6}>
              <FormGroup>
                <Stack gap={6}>
                  {/* Row 1: Water Supply, Electricity Connection */}
                  <Grid fullWidth>
                    <Column sm={4} md={8} lg={8}>
                      {renderDropdown("waterSupply", "Water Supply", false)}
                    </Column>

                    <Column sm={4} md={8} lg={8}>
                      {renderDropdown("electricityConnection", "Electricity Connection", false)}
                    </Column>
                  </Grid>

                  {/* Row 2: Sewage Connection, Width of Facing Road */}
                  <Grid>
                    <Column sm={4} md={8} lg={8}>
                      {renderDropdown(
                        "sewageConnection",
                        "Sewage Connection",
                        false
                      )}
                    </Column>

                    <Column sm={4} md={8} lg={8}>
                      {renderNumberField("widthOfFacingRoad", "Width of Facing Road (ft.)", "width of facing road", false)}
                    </Column>
                  </Grid>

                  {/* Row 4: Gated Security */}
                  <Grid>
                    <Column sm={4} md={8} lg={8}>
                      <Toggle
                        id="gatedSecurity"
                        labelText="Gated Security"
                        labelA="No"
                        labelB="Yes"
                        toggled={form.gatedSecurity}
                        onToggle={(checked) => updateField("gatedSecurity", checked)}
                      />
                    </Column>
                  </Grid>

                  {/* COMMON BUTTONS */}
                  {CommonButtons && (
                    <CommonButtons
                      onSave={handleSaveOnly}
                      onNext={handleSaveAndNext}
                      showBack
                      showSave
                      showNext
                      isSaveLoading={saving}
                    />
                  )}
                </Stack>
              </FormGroup>
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default Amenities;